import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import NewIcon from '@mui/icons-material/Add';
import dateFormat from 'dateformat';
import { Skeleton } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { useTranslation } from 'react-i18next';
import { addEducationsItem, deleteEducationsItem, getEducations, updateEducationsItem } from '../../../../actions/cv';
import TitledSection from '../../../common/TitledSection/TitledSection';
import CVBaseItem from '../../../common/CVBaseItem/CVBaseItem';
import classes from './scss/educations.module.scss';
import BaseModal from '../../../common/ModalWindows/BaseModal';
import AddEditCVRecord from '../../../common/ModalWindows/AddEditCVRecord';
import ValidationErrorLabel from '../../../common/ValidationErrorLabel/ValidationErrorLabel';
import colors from '../../../../scss/colors';
import { usePrevious } from '../../../../utils/hooks';

function Educations() {
  const dispatch = useDispatch();
  const educationsItems = useSelector((state) => state.cv?.educations?.response);
  const pending = useSelector((state) => state.cv?.updatingPending);

  const prevPending = usePrevious(pending);

  const countCurrentEducations = () => educationsItems?.filter((edu) => edu.CurrentlyStudyHere).length;

  const currentEducationsQty = useMemo(() => countCurrentEducations(), [educationsItems]);

  const [openAddWindow, setAddWindow] = useState(false);
  const [preventDelete, setPreventDelete] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    dispatch(getEducations());
  }, []);

  const deleteHandler = (id) => {
    dispatch(deleteEducationsItem(id));
  };

  const isNegativeOffset = new Date().getTimezoneOffset() > 0;

  const addEducation = async (education) => {
    const start = new Date(education.startdate);
    start.setHours(0);
    const end = new Date(education.enddate);
    end.setHours(0);

    dispatch(
      addEducationsItem({
        UniversityName: education.school,
        Degree: education.degree,
        DegreeType: education.degreetype,
        FieldOfStudy: education.fieldofstudy,
        City: 'null',
        State: 'null',
        Country: 'null',
        StartDate: dateFormat(start.toString(), isNegativeOffset ? 'isoUtcDateTime' : 'isoDate'),
        EndDate: dateFormat(end.toString(), isNegativeOffset ? 'isoUtcDateTime' : 'isoDate'),
        CurrentlyStudyHere: education?.currentlyStudyHere || false
      })
    );
  };

  const updateEducation = async (education) => {
    const start = new Date(education.startdate);
    start.setHours(0);
    const end = new Date(education.enddate);
    end.setHours(0);

    dispatch(
      updateEducationsItem({
        Id: education.id,
        UniversityName: education.school,
        Degree: education.degree,
        DegreeType: education.degreetype,
        FieldOfStudy: education.fieldofstudy,
        City: 'null',
        State: 'null',
        Country: 'null',
        StartDate: dateFormat(start.toString(), isNegativeOffset ? 'isoUtcDateTime' : 'isoDate'),
        EndDate: dateFormat(end.toString(), isNegativeOffset ? 'isoUtcDateTime' : 'isoDate'),
        CurrentlyStudyHere: education?.currentlyStudyHere || false
      })
    );
  };

  useEffect(() => {
    if (prevPending === true && pending === false) {
      setAddWindow(false);
    }
  }, [pending]);

  return (
    <TitledSection
      title={t('profile.builder.details.education')}
      actions={
        <div className={classes.actionsWrapper}>
          <div className={classes.actionLabel}>
            <span>{t('common.general.save')}</span> <CheckIcon fontSize="8px" />
          </div>
          <NewIcon role="button" className={classes.action} onClick={() => setAddWindow(true)} />
        </div>
      }
    >
      <div className={classes.educationsList}>
        {openAddWindow && (
          <AddEditCVRecord
            withCurrentlyCheckBox
            open
            closeHandler={() => setAddWindow(false)}
            editeMode={false}
            confirmHandler={addEducation}
            block="educations"
            extra={{ currentEducationsQty }}
            shouldImmediatelySave
            pending={pending}
          />
        )}
        {educationsItems?.length === 0 && (
          <ValidationErrorLabel message={t('dashboard.jobs.emptylist.noeducation')} color={colors.NAVY_BLUE} />
        )}
        {educationsItems ? (
          educationsItems?.map((item) => (
            <CVBaseItem
              withCurrentlyCheckBox
              key={item?.Id || new Date()}
              item={item}
              block="educations"
              deleteItem={() => deleteHandler(item?.Id)}
              editItem={updateEducation}
              extra={{ currentEducationsQty }}
              shouldImmediatelySave
              pending={pending}
            />
          ))
        ) : (
          <Skeleton animation="wave" width="100%" />
        )}
      </div>
      <BaseModal open={preventDelete} closeHandler={() => setPreventDelete(false)} title="Attention" onlyReject>
        <p>
          This section must contain at least one entry.
          <br />
          Please add a new entry first and then delete this one.
        </p>
      </BaseModal>
    </TitledSection>
  );
}

export default Educations;
